import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Plus, Trash2, Save, X, Shield, ShieldCheck, Eye } from "lucide-react";
import api, { formatApiError } from "../lib/api";
import { useAuth } from "../contexts/AuthContext";

const ROLES = [
  { value: "viewer", label: "Lecture seule", labelEn: "Viewer", Icon: Eye, color: "bg-slate-100 text-slate-700" },
  { value: "editor", label: "Éditeur", labelEn: "Editor", Icon: Shield, color: "bg-blue-100 text-blue-800" },
  { value: "admin", label: "Administrateur", labelEn: "Admin", Icon: ShieldCheck, color: "bg-amber-100 text-amber-800" },
];

const roleMeta = (r) => ROLES.find((x) => x.value === r) || ROLES[0];

const UsersManager = ({ tAdmin, lang }) => {
  const { user: currentUser } = useAuth();
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState({ email: "", password: "", name: "", role: "viewer" });
  const [editId, setEditId] = useState(null);
  const [editForm, setEditForm] = useState({ name: "", role: "viewer", password: "" });

  const fetchUsers = async () => {
    setLoading(true);
    try {
      const res = await api.get("/admin/users");
      setUsers(res.data || []);
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Erreur");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchUsers(); }, []);

  const create = async (e) => {
    e.preventDefault();
    try {
      await api.post("/admin/users", form);
      toast.success(tAdmin.users.created);
      setForm({ email: "", password: "", name: "", role: "viewer" });
      setShowForm(false);
      fetchUsers();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Erreur");
    }
  };

  const startEdit = (u) => {
    setEditId(u.id);
    setEditForm({ name: u.name || "", role: u.role || "viewer", password: "" });
  };

  const saveEdit = async (id) => {
    try {
      const payload = { name: editForm.name, role: editForm.role };
      if (editForm.password) payload.password = editForm.password;
      await api.patch(`/admin/users/${id}`, payload);
      toast.success(tAdmin.users.updated);
      setEditId(null);
      fetchUsers();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Erreur");
    }
  };

  const remove = async (u) => {
    if (!window.confirm(tAdmin.users.confirmDelete.replace("{email}", u.email))) return;
    try {
      await api.delete(`/admin/users/${u.id}`);
      toast.success(tAdmin.users.deleted);
      fetchUsers();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Erreur");
    }
  };

  return (
    <div className="bg-white rounded-2xl border border-black/5 p-6" data-testid="users-manager">
      <div className="flex items-center justify-between mb-5">
        <div>
          <h2 className="font-serif text-2xl text-[#0A192F]">{tAdmin.users.title}</h2>
          <p className="text-sm text-[#64748B] mt-1">{tAdmin.users.subtitle}</p>
        </div>
        <button
          onClick={() => setShowForm((s) => !s)}
          className="inline-flex items-center gap-1.5 px-4 py-2 rounded-md bg-[#002B49] text-white hover:bg-[#001F35] text-sm font-medium"
          data-testid="users-add-btn"
        >
          {showForm ? <X size={14} /> : <Plus size={14} />} {showForm ? tAdmin.users.cancel : tAdmin.users.addUser}
        </button>
      </div>

      {showForm && (
        <form onSubmit={create} className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6 bg-[#FDFBF7] p-5 rounded-xl border border-black/5" data-testid="users-create-form">
          <div>
            <label className="block text-xs font-medium text-[#64748B] uppercase tracking-wider mb-1.5">{tAdmin.users.fEmail}</label>
            <input type="email" required value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })}
              className="w-full px-3 py-2 rounded-md border border-black/10 focus:border-[#C5A059] focus:outline-none" data-testid="users-form-email" />
          </div>
          <div>
            <label className="block text-xs font-medium text-[#64748B] uppercase tracking-wider mb-1.5">{tAdmin.users.fName}</label>
            <input type="text" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })}
              className="w-full px-3 py-2 rounded-md border border-black/10 focus:border-[#C5A059] focus:outline-none" data-testid="users-form-name" />
          </div>
          <div>
            <label className="block text-xs font-medium text-[#64748B] uppercase tracking-wider mb-1.5">{tAdmin.users.fPassword}</label>
            <input type="password" required minLength={8} value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })}
              className="w-full px-3 py-2 rounded-md border border-black/10 focus:border-[#C5A059] focus:outline-none" data-testid="users-form-password" />
          </div>
          <div>
            <label className="block text-xs font-medium text-[#64748B] uppercase tracking-wider mb-1.5">{tAdmin.users.fRole}</label>
            <select value={form.role} onChange={(e) => setForm({ ...form, role: e.target.value })}
              className="w-full px-3 py-2 rounded-md border border-black/10 focus:border-[#C5A059] focus:outline-none bg-white" data-testid="users-form-role">
              {ROLES.map((r) => (
                <option key={r.value} value={r.value}>{lang === "en" ? r.labelEn : r.label}</option>
              ))}
            </select>
          </div>
          <div className="md:col-span-2">
            <button type="submit" className="inline-flex items-center gap-1.5 px-4 py-2 rounded-md bg-[#C5A059] text-white hover:bg-[#B08D4A] text-sm font-medium" data-testid="users-form-submit">
              <Save size={14} /> {tAdmin.users.create}
            </button>
          </div>
        </form>
      )}

      {loading ? (
        <div className="p-8 text-center text-[#64748B]">…</div>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-[#FDFBF7] border-b border-black/5">
              <tr className="text-left text-[#64748B] uppercase text-[11px] tracking-wider">
                <th className="px-4 py-3 font-medium">{tAdmin.users.colEmail}</th>
                <th className="px-4 py-3 font-medium">{tAdmin.users.colName}</th>
                <th className="px-4 py-3 font-medium">{tAdmin.users.colRole}</th>
                <th className="px-4 py-3 font-medium">{tAdmin.users.colCreated}</th>
                <th className="px-4 py-3 font-medium text-right">{tAdmin.users.colActions}</th>
              </tr>
            </thead>
            <tbody>
              {users.map((u, i) => {
                const meta = roleMeta(u.role);
                const isEditing = editId === u.id;
                const isSelf = u.id === currentUser?.id;
                return (
                  <tr key={u.id} className="border-b border-black/5 last:border-0" data-testid={`user-row-${i}`}>
                    <td className="px-4 py-3 font-medium text-[#0A192F]">{u.email}{isSelf && <span className="ml-2 text-[10px] uppercase text-[#C5A059]">{tAdmin.users.you}</span>}</td>
                    <td className="px-4 py-3 text-[#334155]">
                      {isEditing ? (
                        <input type="text" value={editForm.name} onChange={(e) => setEditForm({ ...editForm, name: e.target.value })}
                          className="w-full px-2 py-1 rounded border border-black/10 text-sm" />
                      ) : (u.name || "—")}
                    </td>
                    <td className="px-4 py-3">
                      {isEditing ? (
                        <select value={editForm.role} onChange={(e) => setEditForm({ ...editForm, role: e.target.value })}
                          className="px-2 py-1 rounded border border-black/10 text-sm bg-white">
                          {ROLES.map((r) => (<option key={r.value} value={r.value}>{lang === "en" ? r.labelEn : r.label}</option>))}
                        </select>
                      ) : (
                        <span className={`inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full text-[11px] font-medium ${meta.color}`}>
                          <meta.Icon size={11} /> {lang === "en" ? meta.labelEn : meta.label}
                        </span>
                      )}
                    </td>
                    <td className="px-4 py-3 text-[#64748B] whitespace-nowrap">{u.created_at ? new Date(u.created_at).toLocaleDateString() : "—"}</td>
                    <td className="px-4 py-3 text-right">
                      {isEditing ? (
                        <div className="flex justify-end gap-2 items-center flex-wrap">
                          <input type="password" placeholder={tAdmin.users.newPwdPlaceholder} value={editForm.password}
                            onChange={(e) => setEditForm({ ...editForm, password: e.target.value })}
                            className="px-2 py-1 rounded border border-black/10 text-sm w-44" />
                          <button onClick={() => saveEdit(u.id)} className="text-emerald-700 hover:text-emerald-900 text-xs font-medium" data-testid={`user-save-${i}`}>
                            <Save size={14} className="inline" /> {tAdmin.users.save}
                          </button>
                          <button onClick={() => setEditId(null)} className="text-[#64748B] hover:text-[#002B49] text-xs">
                            <X size={14} className="inline" /> {tAdmin.users.cancel}
                          </button>
                        </div>
                      ) : (
                        <div className="flex justify-end gap-3">
                          <button onClick={() => startEdit(u)} className="text-[#002B49] hover:text-[#C5A059] text-xs font-medium" data-testid={`user-edit-${i}`}>
                            {tAdmin.users.edit}
                          </button>
                          {!isSelf && (
                            <button onClick={() => remove(u)} className="text-red-600 hover:text-red-800" data-testid={`user-delete-${i}`}>
                              <Trash2 size={14} />
                            </button>
                          )}
                        </div>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

export default UsersManager;
